import CodeDigitInput from "@/components/CodeDigitInput";
import OitvCard, { OitvCardContent, OitvCardHeader, OitvCardSubtitle, OitvCardTitle } from "@/components/OitvCard";
import { Button } from "@/components/ui/button";
import { APP_VERSION } from "@/config/version";
import { useToast } from "@/contexts/ToastContext";
import { initDeviceAuth, setSessionId } from "@/services";
import { setAuthBasePath } from "@/services/Auth/basepath-service";
import { ApiError, AuthStep } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";

/**
 * Schema de validação para o formulário de ativação de dispositivo
 * Valida que o código tenha exatamente 6 caracteres alfanuméricos
 */
const deviceActivationSchema = z.object({
  code: z.array(z.string()).refine(
    (arr) => arr.length === 6 && arr.every(char => char.length === 1 && /^[a-zA-Z0-9]$/.test(char)),
    "Código deve ter exatamente 6 caracteres alfanuméricos"
  ),
});

/**
 * Tipo inferido do schema de validação do formulário de ativação
 */
type DeviceActivationFormData = z.infer<typeof deviceActivationSchema>;

/**
 * Componente de ativação de dispositivo
 * Permite ao usuário inserir um código de 6 caracteres para conectar um dispositivo à sua conta Oi TV
 * 
 * @returns {JSX.Element} Componente React de ativação de dispositivo
 */
const DeviceActivation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting }
  } = useForm<DeviceActivationFormData>({
    resolver: zodResolver(deviceActivationSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: new Array(6).fill(""),
    },
  });

  const code = watch("code");

  /**
   * Pré-preenche o código a partir do query parameter ?usercode= na URL
   * Remove caracteres não alfanuméricos e converte para maiúsculo
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const usercode = (searchParams.get("usercode") || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 6);
    if (usercode) {
      const prefilled = new Array(6).fill("");
      for (let i = 0; i < usercode.length && i < 6; i++) {
        prefilled[i] = usercode[i];
      }
      setValue("code", prefilled);
    }
  }, [location.search, setValue]);

  /**
   * Manipula a mudança no código digitado pelo usuário
   * @param {string[]} newCode - Array com os novos valores do código
   */
  const handleCodeChange = (newCode: string[]) => {
    setValue("code", newCode);
  };

  /**
   * Verifica se o código está completo (todos os 6 dígitos preenchidos)
   */
  const isCodeComplete = code.every((digit: string) => digit !== "");

  /**
   * Manipula o envio do formulário de ativação de dispositivo
   * Valida o código com a API e redireciona baseado no próximo passo
   * 
   * @param {DeviceActivationFormData} data - Dados do formulário contendo o código
   */
  const onSubmit = async (data: DeviceActivationFormData) => {
    const enteredCode = data.code.join("");
    
    try {
      const response = await initDeviceAuth({ userCode: enteredCode });
      if (response?.sessionId) {
        setSessionId(response.sessionId);
      }
      // Sinalizar que a partir daqui o fluxo deve usar /device-auth
      setAuthBasePath('/device-auth');
      showToast({ title: "Código aceito! Sessão iniciada.", type: "success" });
      
      // Redirecionar baseado no nextStep retornado pela API
      if (response.nextStep === AuthStep.VALIDATE_DOCUMENT) {
        navigate("/");
      } else {
        // Para outros steps, retornar erro
        throw new Error(`Step não suportado: ${response.nextStep}`);
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error("Erro ao iniciar device auth:", apiError);
      showToast({ title: apiError?.response?.data?.message || "Código inválido ou expirado.", type: "error" });
      
      // Limpar o código em caso de erro
      setValue("code", new Array(6).fill(""));
    }
  };

  return (
    <div className="min-h-svh bg-background-gradient-primary">
      <div className="flex flex-col items-center justify-center min-h-screen px-5">
        <div className="w-full max-w-lg">
          <OitvCard>
            <OitvCardHeader>
              <OitvCardTitle>Conectar Dispositivo</OitvCardTitle>
              <OitvCardSubtitle>Digite o código exibido no dispositivo que deseja conectar à sua conta Oi TV</OitvCardSubtitle>
            </OitvCardHeader>
            <OitvCardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 h-[300px] flex flex-col justify-between">
                <CodeDigitInput
                  values={code}
                  onChange={handleCodeChange}
                  disabled={isSubmitting}
                  length={6}
                  acceptText={true}
                />
                <Button 
                  type="submit"
                  className="w-full bg-primary hover:bg-green-600" 
                  disabled={!isCodeComplete || isSubmitting}
                >
                  {isSubmitting ? "Ativando..." : "Continuar"}
                </Button>
              </form>
            </OitvCardContent>
          </OitvCard>
          <div className="flex justify-center items-center mt-4">
            <span className="text-xs text-white">{APP_VERSION}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceActivation;


